// Las dos respuestas del repaso: un acierto al acertar y un «oooh» al fallar.
//
//     node scripts/prueba_sonidosDelRepaso.mjs            (desde frontend/)
//     node scripts/prueba_sonidosDelRepaso.mjs --prueba   y que las reglas muerden
//
// LO PIDIÓ GABRI EL 7 DE OCTUBRE DE 2026, mirando el repaso del Olvido: *«se deberían incluir
// sonidos de acierto y de fallo; debe ser distinguible»*. Entonces pidió palmas contadas, se
// hicieron tres, y el 14 eligió para el repaso un fichero de verdad. Al oírlo dijo lo que
// hacía falta oír: *«no es un sonido de palmas como tal, es otro sonido de acierto»*. Y el 17,
// aún más claro: *«olvídate de la palabra palmada; sólo hay unas palmadas de fondo con otro
// sonido»*. **Esto ya no son palmas y no se llaman así en ningún sitio.**
//
// LO QUE HAY AQUÍ ES EL RESPALDO, no lo que se oye normalmente: el fichero `repasada` es lo que
// suena, y esto entra cuando no se ha podido cargar. Por eso tiene que PARECERSE A ÉL. Hasta
// hoy no se parecía en nada —eran tres golpes graves espaciados 0,17 s— y Gabri lo oyó como lo
// que era: *«no carga el sonido, pero suenan dos sonidos de victoria distintos»*. Un respaldo
// que no se parece al sonido bueno no es un respaldo: es un segundo sonido.
//
// SE DISEÑÓ CONTRA LA MEDIDA DEL FICHERO, que es la única forma de saber si se parece:
//
//   |                        | centroide | <200 Hz | 800-2500 Hz | golpes |
//   | `repasada.m4a`         |  4180 Hz  |    0 %  |     66 %    |   1    |
//   | esto, antes            |  1906 Hz  |   37 %  |     16 %    |   3    |
//   | esto, ahora            |  3138 Hz  |    0 %  |     67 %    |   1    |
//
// Queda algo más brillante que el fichero y eso se acepta: pasarse de agudo se oye como un
// respaldo pobre, y quedarse grave se oye como **otro sonido**, que es lo que se vino a quitar.
//
// Y EL «OOOH» NO ABUCHEA. Es la regla de la casa escrita en `sonidosDelJuego.mjs` —*en esta
// casa fallar enseña y la pregunta vuelve a salir*— y por eso son voces suaves que **caen** un
// poco, desafinadas entre sí para que suenen a varios y no a uno, y más flojas que el acierto.
// Un «oooh» que suena más que el acierto convierte el repaso en un concurso.
//
// Esto es RUIDO y no notas, y no es un detalle de implementación: un golpe hecho con un
// oscilador es un pitido corto. Por eso va por el generador de ruido de la casa.
//
// Lógica pura, sin audio.

/**
 * EL ACIERTO DEL REPASO, DE RESPALDO. Tres capas que suenan **a la vez** y hacen un golpe, no
 * tres golpes seguidos.
 *
 *   · **golpe**: lo que llega primero y da el brillo. Es el que pone el centroide arriba.
 *   · **cuerpo**: el peso, corto. Sin él queda un siseo.
 *   · **cola**: lo que se queda sonando medio segundo. Es el sitio donde pasa la cosa: el
 *     fichero tiene ahí sus palmadas de fondo, y aquí es lo que ocupa su lugar.
 *
 * NINGUNA BAJA DE 200 Hz, y esa es la regla que lo vigila. El fichero no tiene nada ahí, y el
 * día que alguien meta un grave vuelve el golpe sordo que sonaba a otra cosa.
 */
export const ACIERTO_DEL_REPASO = {
  golpe: { tipo: "bandpass", desde: 3600, hasta: 1400, dur: 0.07, gain: 0.17, q: 0.9 },
  cuerpo: { tipo: "bandpass", desde: 900, hasta: 350, dur: 0.12, gain: 0.115, q: 0.8 },
  cola: { tipo: "bandpass", desde: 2200, hasta: 800, dur: 0.5, gain: 0.05, q: 0.7, when: 0.02 },
};

/** Las tres capas desplegadas, listas para el generador de ruido de la casa. */
export function ruidosDelAcierto() {
  return [ACIERTO_DEL_REPASO.golpe, ACIERTO_DEL_REPASO.cuerpo, ACIERTO_DEL_REPASO.cola]
    .map((c) => ({ ...c, when: c.when || 0 }));
}

/**
 * Y LA TARJETA QUE SE GUARDA: «ya me acuerdo». Lo pidió Gabri el mismo día: *«en las tarjetas
 * debería sonar algo cuando le das a ya me acuerdo»*.
 *
 * NO ES UN ACIERTO, Y POR ESO NO SUENA COMO UNO: mirar una tarjeta no es acertarla —lo dice el
 * propio repaso, que no la puntúa—, así que esto no sube ni celebra. Es el ruido de guardar
 * algo: un roce corto y seco que se va hacia abajo, como una ficha que entra en su sitio.
 */
export const GUARDADA = [
  { tipo: "lowpass", desde: 1400, hasta: 420, dur: 0.12, gain: 0.105, q: 0.7 },
];

/**
 * EL «OOOH»: tres voces que caen una tercera, desafinadas entre sí.
 *
 * Sol–do–sol, que es un acorde abierto y sin color: no es triste ni alegre, es **gente**. Las
 * tres bajan a la vez —la convención de la casa, fallar baja— y la de arriba va cuatro hercios
 * desafinada, que es el truco de siempre para que dos voces suenen a muchas.
 */
export const OOOH = [
  { freq: 294, to: 233, type: "sine", dur: 0.62, gain: 0.1, when: 0.05 },
  { freq: 147, to: 117, type: "sine", dur: 0.68, gain: 0.09, when: 0.05 },
  { freq: 297, to: 236, type: "triangle", dur: 0.56, gain: 0.05, when: 0.09 },
];

/** Cuánto dura cada uno de principio a fin. */
export const loQueDura = (lista) =>
  Math.max(...lista.map((n) => (n.when || 0) + n.dur));
